import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Task, TaskStatus, TaskPriority, TaskEntityType, ChecklistItem } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'
import { notifyAthlete } from './portalNotify'
import { logger } from '../utils/logger'

export type TaskPayload = {
  title: string // Required
  description?: string | null
  status: TaskStatus // Required
  priority?: TaskPriority | null
  due_date?: string | null
  completed_at?: string | null
  assigned_to?: string | null
  assigned_by?: string | null
  entity_type?: TaskEntityType | null
  entity_id?: string | null
  client_id?: string | null
  deal_id?: string | null
  // Client obligation fields
  visible_to_client?: boolean
  client_instructions?: string | null
  task_category?: string | null
  location?: string | null
  event_time?: string | null
  checklist?: ChecklistItem[] | null
  tags?: string[] | null
  is_recurring?: boolean
  recurrence_rule?: string | null
  agency_id?: string
  created_by?: string | null
}

export type TaskFilters = {
  status?: TaskStatus
  assigned_to?: string
  entity_type?: TaskEntityType
  entity_id?: string
  client_id?: string
  deal_id?: string
  overdue?: boolean
  due_before?: string // ISO date string
  due_after?: string // ISO date string
  limit?: number // Limit number of results returned
}


export type PaginatedTasksResult = {
  items: Task[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

function mapTask(record: Record<string, any>): Task {
  return {
    id: record.id,
    title: record.title ?? '',
    description: record.description ?? null,
    status: (record.status ?? 'todo') as TaskStatus,
    priority: record.priority ?? null,
    due_date: record.due_date ?? null,
    completed_at: record.completed_at ?? null,
    assigned_to: record.assigned_to ?? null,
    assigned_by: record.assigned_by ?? null,
    entity_type: record.entity_type ?? null,
    entity_id: record.entity_id ?? null,
    client_id: record.client_id ?? null,
    deal_id: record.deal_id ?? null,
    visible_to_client: record.visible_to_client ?? false,
    client_instructions: record.client_instructions ?? null,
    task_category: record.task_category ?? null,
    location: record.location ?? null,
    event_time: record.event_time ?? null,
    checklist: Array.isArray(record.checklist) ? record.checklist : null,
    tags: Array.isArray(record.tags) ? record.tags : null,
    is_recurring: record.is_recurring ?? false,
    recurrence_rule: record.recurrence_rule ?? null,
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listTasks(filters?: TaskFilters) {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  // If no agency, return empty - user needs to complete onboarding
  if (!agencyId) {
    return []
  }

  const filterParts: string[] = [`agency_id = "${escapeFilterValue(agencyId)}"`]

  if (filters?.status) {
    filterParts.push(`status = "${escapeFilterValue(filters.status)}"`)
  }
  if (filters?.assigned_to) {
    filterParts.push(`assigned_to = "${escapeFilterValue(filters.assigned_to)}"`)
  }
  // Entity filtering is done client-side via listTasksByEntity() function
  // which checks entity_type/entity_id and deal_id/client_id fields
  if (filters?.due_before) {
    filterParts.push(`due_date <= "${escapeFilterValue(filters.due_before)}"`)
  }
  if (filters?.due_after) {
    filterParts.push(`due_date >= "${escapeFilterValue(filters.due_after)}"`)
  }

  const filter = filterParts.join(' && ')

  // Fetch with a reasonable default limit using getList instead of getFullList
  const limit = filters?.limit && filters.limit > 0 ? filters.limit : 200

  const result = await pb.collection('tasks').getList<Record<string, any>>(1, limit, {
    filter,
  })

  let tasks = result.items.map(mapTask)
    
  // Sort by due_date (ascending), then by created date (descending) for tasks without due dates
  tasks.sort((a, b) => {
    // Tasks with due dates come first, sorted by due date
    if (a.due_date && b.due_date) {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    }
    if (a.due_date && !b.due_date) return -1
    if (!a.due_date && b.due_date) return 1
    // Both have no due date - sort by created date (newest first)
    return new Date(b.created).getTime() - new Date(a.created).getTime()
  })
    
  // Filter overdue tasks if requested
  if (filters?.overdue) {
    const now = new Date()
    tasks = tasks.filter(task => {
      if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false
      return new Date(task.due_date) < now
    })
  }

  return tasks
}



export async function listTasksPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedTasksResult> {
  
  // Get current user's agency for filtering (security: agency isolation)
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) {
    return { items: [], page: 1, perPage, totalItems: 0, totalPages: 0 }
  }

  // Always filter by agency_id for security
  const agencyFilter = `agency_id = "${escapeFilterValue(agencyId)}"`
  const filter = options?.filter ? `(${agencyFilter}) && (${options.filter})` : agencyFilter

  const result = await pb.collection('tasks').getList<Record<string, any>>(page, perPage, {
    sort: options?.sort ?? '-created',
    filter,
  })
  return {
    items: result.items.map(mapTask),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

export async function getTask(taskId: string) {
  try {
    const record = await pb.collection('tasks').getOne<Record<string, any>>(taskId)
    return mapTask(record)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      throw new Error('Task not found')
    }
    throw error
  }
}

/**
 * Email and push to the athlete for a task they can see in the portal.
 * Fire-and-forget: the task is already saved, and a notification failure
 * is logged rather than surfaced as a save failure.
 */
function notifyActionItem(task: Task) {
  if (!task.client_id) return
  notifyAthlete('action_item', task.client_id, {
    task_title: task.title,
    due_date: task.due_date || undefined,
    instructions: task.client_instructions || undefined,
    deal_id: task.entity_type === 'deal' ? task.entity_id || undefined : undefined,
  }).catch(err => logger.error('Athlete action-item notification failed', task.id, err))
}

export async function createTask(payload: TaskPayload) {
  try {
    // Ensure user has an agency assigned (auto-create if needed)
    const userAgencyId = await ensureUserAgency()
    
    const createPayload: any = {
      ...payload,
      agency_id: userAgencyId
    }
    
    const record = await pb.collection('tasks').create(createPayload)
    const task = mapTask(record)
    if (task.visible_to_client && task.client_id) {
      notifyActionItem(task)
    }
    return task
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      throw new Error('Failed to create task. Please ensure the tasks collection exists in PocketBase.')
    }
    throw error
  }
}

export async function updateTask(
  taskId: string,
  payload: Partial<TaskPayload>,
) {
  try {
    // The athlete is told once, when a task first becomes visible to them,
    // not on every later edit. That needs the previous value.
    let wasVisible: boolean | null = null
    if (payload.visible_to_client === true) {
      const previous = await pb.collection('tasks').getOne(taskId)
      wasVisible = Boolean(previous.visible_to_client)
    }

    const record = await pb.collection('tasks').update(taskId, payload)
    const task = mapTask(record)

    if (wasVisible === false && task.client_id) {
      notifyActionItem(task)
    }

    // Fire-and-forget: create next instance of recurring task when completed
    if (payload.status === 'completed' && record.is_recurring && record.recurrence_rule) {
      import('../utils/recurringTaskProcessor').then(({ processRecurringTask }) => {
        processRecurringTask(taskId).catch(err => {
          console.warn('Recurring task processing failed (non-blocking):', err)
        })
      })
    }

    return task
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      throw new Error('Failed to update task')
    }
    throw error
  }
}

export async function deleteTask(taskId: string) {
  try {
    await pb.collection('tasks').delete(taskId)
  } catch (error: any) {
    const status = error?.status || error?.response?.status
    if (status === 404 || status === 500 || status === 400) {
      throw new Error('Failed to delete task')
    }
    throw error
  }
}

export async function listTasksByEntity(entityType: TaskEntityType, entityId: string) {
  const allTasks = await listTasks()
  return allTasks.filter(task => {
    // Check direct relation fields first
    if (entityType === 'deal' && task.deal_id === entityId) {
      return true
    }
    if (entityType === 'client' && task.client_id === entityId) {
      return true
    }
    // Check generic entity_type/entity_id fields
    if (task.entity_type === entityType && task.entity_id === entityId) {
      return true
    }
    return false
  })
}

export async function listOverdueTasks() {
  return listTasks({ overdue: true })
}

export async function listTasksByAssignee(userId: string) {
  return listTasks({ assigned_to: userId })
}

export function isTaskOverdue(task: Task): boolean {
  if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return false
  return new Date(task.due_date) < new Date()
}

export function getTaskStatusColor(status: TaskStatus): string {
  switch (status) {
    case 'completed':
      return '#4E9A6A' // green
    case 'in_progress':
      return '#4A5561' // blue
    case 'waiting':
      return '#A8873F' // purple
    case 'cancelled':
      return '#A2A8B0' // gray
    case 'todo':
    default:
      return '#C08A3C' // amber
  }
}

export function formatTaskDueDate(dueDate: string | null): string {
  if (!dueDate) return 'No due date'
  const date = new Date(dueDate)
  const now = new Date()
  const diffTime = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays < 0) return 'Overdue'
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  if (diffDays <= 7) return `Due in ${diffDays} days`
  return date.toLocaleDateString()
}
